import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, UserPlus, Download, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiError, setToken, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { NotifyPrefs } from "@/components/NotifyPrefs";
import { ClipPushToggle } from "@/components/ClipPushToggle";
import { ProfileEditor } from "@/components/ProfileEditor";

const inputCls = "rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#2f7bff] w-full";

export default function Workspace() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [members, setMembers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "member" });
  const load = () => api.get("/workspace/members").then((r) => setMembers(r.data));
  useEffect(() => { load(); }, []);
  const canManage = ["owner", "admin"].includes(user?.role);
  const deleteAccount = async () => {
    if (!window.confirm(t("pv.confirm"))) return;
    try { await api.delete("/privacy/account"); setToken(null); toast.success(t("pv.deleted")); window.location.href = "/login"; } catch (e) { toast.error(formatApiError(e)); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/workspace/members", f);
      toast.success(t("ws.added"));
      setF({ name: "", email: "", password: "", role: "member" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 2xl:px-14 py-6 sm:py-10">
      <div className="cx-label">{t("ws.tenant")}</div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl mt-3"><span className="cx-chrome">{t("ws.title1")}</span><span className="cx-text-grad">{t("ws.title2")}</span></h1>
      <div className="grid xl:grid-cols-[1fr_1fr] gap-6 mt-8 sm:mt-10">
        <div className="space-y-6">
          {user?.role === "owner" && <ProfileEditor />}
          {user?.role !== "demo" && <NotifyPrefs />}
          <ClipPushToggle />
          <div className="cx-card cx-brackets rounded-2xl p-6 grid sm:grid-cols-3 gap-6" data-testid="workspace-info">
            <div className="min-w-0">
              <div className="text-sm text-slate-400">{t("ws.name")}</div>
              <div className="font-display text-xl sm:text-2xl text-white truncate">{user?.workspace?.name}</div>
            </div>
            <div className="min-w-0">
              <div className="text-sm text-slate-400">{t("ws.id_hint")}</div>
              <button data-testid="copy-workspace-id-btn" onClick={() => { navigator.clipboard.writeText(user.tenant_id); toast.success(t("ws.copied")); }} className="font-mono text-lg text-[#3ad7ff] flex items-center gap-2 mt-1 max-w-full">
                <span className="truncate">{user?.tenant_id}</span> <Copy className="w-4 h-4 shrink-0" />
              </button>
            </div>
            <div>
              <div className="text-sm text-slate-400">{t("ws.role")}</div>
              <div className="font-mono text-white uppercase mt-1">{user?.role}</div>
            </div>
          </div>
          {canManage ? (
            <form onSubmit={add} className="cx-card rounded-2xl p-6" data-testid="add-member-form">
              <div className="cx-label flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> {t("ws.add")}</div>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <input data-testid="member-name-input" className={inputCls} placeholder={t("ws.nickname")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
                <input data-testid="member-email-input" className={inputCls} type="email" placeholder={t("auth.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
                <input data-testid="member-password-input" className={inputCls} type="password" placeholder={t("auth.password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={6} />
                <select data-testid="member-role-select" className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                  <option value="member">{t("ws.member")}</option>
                  <option value="admin">{t("ws.admin")}</option>
                </select>
              </div>
              <button data-testid="add-member-submit-btn" className="cx-btn rounded-full px-6 py-2.5 text-sm font-semibold mt-4">{t("ws.add_btn")}</button>
            </form>
          ) : user?.role === "demo" && (
            <div className="cx-card rounded-2xl p-6 text-sm text-slate-400" data-testid="demo-note">{t("ws.demo_note")}</div>
          )}
          <div className="cx-card rounded-2xl p-6" data-testid="privacy-panel">
            <div className="cx-label flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> {t("pv.title")}</div>
            <div className="flex flex-wrap gap-3 mt-4">
              <a data-testid="privacy-export-btn" href="#export" onClick={(e) => { e.preventDefault(); downloadFile("/privacy/export", "clipx-mes-donnees.json").catch((er) => toast.error(formatApiError(er))); }} className="cx-ghost rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><Download className="w-4 h-4" /> {t("pv.export")}</a>
              {user?.role !== "demo" && <button data-testid="privacy-delete-btn" onClick={deleteAccount} className="rounded-full px-5 py-2.5 text-sm flex items-center gap-2 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition-colors"><Trash2 className="w-4 h-4" /> {t("pv.delete")}</button>}
            </div>
          </div>
        </div>
        <div className="cx-card rounded-2xl h-fit" data-testid="members-list">
          <div className="px-6 pt-5 pb-3 cx-label">{t("ws.members")} · {members.length}</div>
          <div className="divide-y divide-white/5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 px-6 py-4" data-testid="member-row">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{m.name}</div>
                  <div className="text-xs text-slate-500 truncate">{m.email}</div>
                </div>
                <span className="font-mono text-[11px] uppercase px-2 py-1 rounded border border-white/10 text-slate-300 shrink-0">{m.role}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
